import CancelButton from 'components/buttons/CancelButton';
import SaveButton from 'components/buttons/SaveButton';
import CircularLoader from 'components/CircularLoader';
import { FormattedDate } from 'components/misc/FormattedDate';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Checkbox } from 'components/ui/checkbox';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { ReactNode, useMemo, useState } from 'react';
import { useGetExpensesWithFiltersQuery, useSwitchExpensesMatterMutation } from 'store/api/expenses';
import { useGetPaginatedTimecardsWithFiltersQuery, useSwitchTimecardsMatterMutation } from 'store/api/timecards';
import { ExpenseListItem, ExpenseStatusType } from 'types/expenses';
import { DelegationAlert } from 'pages/delegations/DelegationAlert';
import { TransferDestinationCase } from 'types/matters';
import { TimecardListItemData, TimecardStatusType } from 'types/timecards';
import { errorAlert, successAlert } from 'utils/alert';
import { useHasPermissions } from 'utils/permissionUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { secondsToHours } from 'utils/time';
import { useClassifiers } from 'utils/useClassifiers';

/**
 * The source Case is an offer status Case, so its item counts are small; one generous page stands in
 * for pagination the selection list does not offer.
 */
const ITEM_PAGE_SIZE = 500;

const USED_CLASSIFIERS = ['MATTER_STATE_TYPE'];

interface Props {
    /** The Offer Won Case the items currently sit on. */
    sourceMatterId: number;
    /** The Case just opened from the Offer Won Case, which selected items are moved to. */
    destinationMatter: TransferDestinationCase;
    onClose: () => void;
}

interface TransferItem {
    id: number;
    /** Short accessible name for the row's checkbox. */
    label: string;
    primary: ReactNode;
    secondary: ReactNode;
    isTransferable: boolean;
}

interface TransferGroupProps {
    heading: string;
    items: TransferItem[];
    selectedIds: number[];
    onSelectedIdsChange: (ids: number[]) => void;
    disabled: boolean;
}

/**
 * `switchTimecardsMatter` moves a timecard only when its stored status is FINISHED and
 * `canUserEditTimecard` allows it. The status on a list row is derived rather than stored — a FINISHED
 * timecard on a non-billable case is reported as DONE — so DONE has to be accepted here, and `isEditable`
 * (the same `canUserEditTimecard` answer) is what separates those from a stored DONE, an invoiced or an
 * out-of-window timecard the endpoint would reject. A DRAFT of one's own is editable but never movable,
 * hence the status check as well.
 */
export function isTransferableTimecard(timecard: TimecardListItemData): boolean {
    return (
        (timecard.timecardStatusTypeCode === TimecardStatusType.FINISHED ||
            timecard.timecardStatusTypeCode === TimecardStatusType.DONE) &&
        timecard.isEditable
    );
}

/** `switchExpensesMatter` goes through `canUserEditExpense`, which accepts an editable FINISHED expense. */
export function isTransferableExpense(expense: ExpenseListItem): boolean {
    return expense.expenseStatusTypeCode === ExpenseStatusType.FINISHED && expense.isEditable;
}

function toggleId(ids: number[], id: number, isSelected: boolean): number[] {
    return isSelected ? [...ids, id] : ids.filter((selectedId) => selectedId !== id);
}

function TransferGroup({ heading, items, selectedIds, onSelectedIdsChange, disabled }: TransferGroupProps) {
    if (items.length === 0) {
        return null;
    }

    const transferableIds = items.filter((item) => item.isTransferable).map((item) => item.id);
    const selectedCount = transferableIds.filter((id) => selectedIds.includes(id)).length;
    const areAllSelected = transferableIds.length > 0 && selectedCount === transferableIds.length;

    return (
        <section className="flex flex-col">
            <div className="flex items-center gap-3 pl-3">
                <Checkbox
                    variant="secondary"
                    aria-label={heading}
                    disabled={disabled || transferableIds.length === 0}
                    checked={areAllSelected}
                    indeterminate={selectedCount > 0 && !areAllSelected}
                    onCheckedChange={(checked) => {
                        const withoutGroup = selectedIds.filter((id) => !transferableIds.includes(id));
                        onSelectedIdsChange(checked ? [...withoutGroup, ...transferableIds] : withoutGroup);
                    }}
                />
                <h5 className="text-h5 py-2">{heading}</h5>
            </div>
            <ul>
                {items.map((item) => (
                    <li key={item.id} className="border-divider flex items-center gap-3 pl-3 not-last:border-b">
                        <Checkbox
                            variant="secondary"
                            aria-label={item.label}
                            disabled={disabled || !item.isTransferable}
                            checked={selectedIds.includes(item.id)}
                            onCheckedChange={(checked) => onSelectedIdsChange(toggleId(selectedIds, item.id, checked))}
                        />
                        <div className="flex flex-col py-2">
                            <span className="text-body1">{item.primary}</span>
                            <span className="text-caption text-fg-secondary">{item.secondary}</span>
                            {item.isTransferable ? null : (
                                <span className="text-caption text-fg-disabled">
                                    {t('matters.transfer-not-transferable')}
                                </span>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}

export function TransferTimeAndExpensesModal({ sourceMatterId, destinationMatter, onClose }: Props): JSX.Element {
    const formatEmployeeName = useEmployeeNameFormatter();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const [selectedTimecardIds, setSelectedTimecardIds] = useState<number[]>([]);
    const [selectedExpenseIds, setSelectedExpenseIds] = useState<number[]>([]);
    // Each endpoint demands its own matter-access privilege, so without it the whole batch would 403.
    const canTransferTimecards = useHasPermissions(['UPDATE.TIMECARDS.MATTER_ACCESS']);
    const canTransferExpenses = useHasPermissions(['UPDATE.EXPENSES.MATTER_ACCESS']);
    const [switchTimecardsMatter, { isLoading: isMovingTimecards }] = useSwitchTimecardsMatterMutation();
    const [switchExpensesMatter, { isLoading: isMovingExpenses }] = useSwitchExpensesMatterMutation();

    const {
        data: timecardsData,
        isLoading: isLoadingTimecards,
        isError: isTimecardsError,
    } = useGetPaginatedTimecardsWithFiltersQuery({
        page: 0,
        pageSize: ITEM_PAGE_SIZE,
        sort: ['timecardDate,desc', 'stopwatchRunningSince,desc'],
        filters: `matter.id==(${sourceMatterId})`,
    });

    const {
        data: expensesData,
        isLoading: isLoadingExpenses,
        isError: isExpensesError,
    } = useGetExpensesWithFiltersQuery({
        page: 0,
        pageSize: ITEM_PAGE_SIZE,
        sort: 'expenseDate,desc',
        filters: `matter.id==${sourceMatterId}`,
    });

    const timecardItems = useMemo<TransferItem[]>(
        () =>
            (timecardsData?.content ?? []).map((timecard) => ({
                id: timecard.id,
                label: timecard.description || `#${timecard.id}`,
                primary: (
                    <>
                        <HoursDisplay value={secondsToHours(timecard.totalTimeInSeconds)} />
                        {` - ${timecard.description}`}
                    </>
                ),
                secondary: (
                    <>
                        <FormattedDate value={timecard.timecardDate} />
                        {` - ${formatEmployeeName(timecard.employee)} - `}
                        {timecard.isNonBillable ? t('timecards.form.is-non-billable') : t('timecards.form.billable')}
                    </>
                ),
                isTransferable: canTransferTimecards && isTransferableTimecard(timecard),
            })),
        [timecardsData, formatEmployeeName, canTransferTimecards]
    );

    const expenseItems = useMemo<TransferItem[]>(
        () =>
            (expensesData?.content ?? []).map((expense) => ({
                id: expense.id,
                label: expense.description || `#${expense.id}`,
                primary: (
                    <>
                        {`${expense.description} - `}
                        <MoneyDisplay amount={expense.expenseAmount ?? 0} currency={expense.currencyTypeCode} />
                    </>
                ),
                secondary: (
                    <>
                        <FormattedDate value={expense.expenseDate} />
                        {' - '}
                        {expense.isNonBillable ? t('timecards.form.is-non-billable') : t('timecards.form.billable')}
                    </>
                ),
                isTransferable: canTransferExpenses && isTransferableExpense(expense),
            })),
        [expensesData, canTransferExpenses]
    );

    // The destination status is a classifier label, so it waits with the rest rather than flashing its code.
    const isLoading = isLoadingTimecards || isLoadingExpenses || isLoadingClassifiers;
    const isMoving = isMovingTimecards || isMovingExpenses;
    const hasLoadError = isTimecardsError || isExpensesError;
    // A failed load must never read as "nothing to transfer": that would invite the user to skip a transfer they need.
    const isEmpty = !isLoading && !hasLoadError && timecardItems.length === 0 && expenseItems.length === 0;
    const hasSelection = selectedTimecardIds.length > 0 || selectedExpenseIds.length > 0;

    const transfer = async () => {
        let hasFailure = false;

        if (selectedTimecardIds.length > 0) {
            try {
                await switchTimecardsMatter({
                    currentMatterId: sourceMatterId,
                    newMatterId: destinationMatter.id,
                    newMatterAssignmentId: null,
                    timecardIds: selectedTimecardIds,
                }).unwrap();
                setSelectedTimecardIds([]);
                successAlert(t('matters.timecard-is-moved'));
            } catch (error) {
                hasFailure = true;
                logError(error);
                errorAlert(t('matters.transfer-timecards-failed'));
            }
        }

        if (selectedExpenseIds.length > 0) {
            try {
                await switchExpensesMatter({
                    currentMatterId: sourceMatterId,
                    newMatterId: destinationMatter.id,
                    newMatterAssignmentId: null,
                    expenseIds: selectedExpenseIds,
                }).unwrap();
                setSelectedExpenseIds([]);
                successAlert(t('matters.expense-is-moved'));
            } catch (error) {
                hasFailure = true;
                logError(error);
                errorAlert(t('matters.transfer-expenses-failed'));
            }
        }

        // Whatever moved is already gone from the source Case, so a failed group can be retried on the
        // refreshed list instead of losing the user's remaining selection to a close.
        if (!hasFailure) {
            onClose();
        }
    };

    const continueButton = (
        <CancelButton key="continue" onClick={onClose} disabled={isMoving}>
            {t('matters.continue-without-transferring')}
        </CancelButton>
    );

    return (
        <>
            <ModalHeader title={t('matters.transfer-time-and-expenses')} onCancel={onClose} />
            <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-8 py-6">
                {isLoading ? (
                    <CircularLoader className="static py-4" />
                ) : (
                    <>
                        <h5 className="text-h5">
                            {t('matters.transferring-to', {
                                caseName: `${destinationMatter.name} (${getClassifierText(
                                    classifiers['MATTER_STATE_TYPE'],
                                    destinationMatter.matterStateTypeCode
                                )})`,
                            })}
                        </h5>
                        <p className="text-body1">
                            {isEmpty ? t('matters.transfer-empty-state') : t('matters.transfer-time-and-expenses-body')}
                        </p>
                        {hasLoadError ? (
                            <DelegationAlert severity="error">{t('matters.transfer-load-error')}</DelegationAlert>
                        ) : null}
                        <TransferGroup
                            heading={t('matters.timecards')}
                            items={timecardItems}
                            selectedIds={selectedTimecardIds}
                            onSelectedIdsChange={setSelectedTimecardIds}
                            disabled={isMoving}
                        />
                        <TransferGroup
                            heading={t('expenses.expenses')}
                            items={expenseItems}
                            selectedIds={selectedExpenseIds}
                            onSelectedIdsChange={setSelectedExpenseIds}
                            disabled={isMoving}
                        />
                    </>
                )}
            </div>
            <ModalFooterSimple
                buttons={
                    isEmpty
                        ? [continueButton]
                        : [
                              continueButton,
                              <SaveButton
                                  key="transfer"
                                  onClick={() => void transfer()}
                                  disabled={!hasSelection || isMoving}
                              >
                                  {t('matters.transfer-selected')}
                              </SaveButton>,
                          ]
                }
            />
        </>
    );
}
